const GENERIC_PRODUCT_TERMS = new Set([
  't shirt',
  't shirts',
  'tshirt',
  'tshirts',
  'tee',
  'tees',
  'polo',
  'polos',
  'shirt',
  'shirts',
  'hoodie',
  'hoodies',
  'sweatshirt',
  'sweatshirts',
  'sweater',
  'sweaters',
  'jean',
  'jeans',
  'dress',
  'dresses',
  'jacket',
  'jackets',
  'shoe',
  'shoes',
  'sneaker',
  'sneakers',
  'bag',
  'bags',
  'wallet',
  'wallets',
  'hat',
  'hats',
  'cap',
  'caps',
]);

export interface CatalogProductMatchInput {
  name: string;
  keywords?: string[];
  identifier?: string;
  category?: string;
}

export interface ListingMatchInput {
  title?: string;
  description?: string;
  product?: string;
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function termVariants(term: string): string[] {
  const base = normalize(term);
  if (!base) return [];
  const out = new Set([base, base.replace(/\s+/g, '')]);
  if (base.endsWith('es') && base.length > 4) out.add(base.slice(0, -2));
  else if (base.endsWith('s') && base.length > 3) out.add(base.slice(0, -1));
  else {
    out.add(`${base}s`);
    out.add(`${base}es`);
  }
  return Array.from(out);
}

function containsTerm(haystack: string, term: string): boolean {
  return termVariants(term).some(variant => {
    if (!variant) return false;
    if (!variant.includes(' ')) {
      return new RegExp(`(?:^|\\s)${variant}(?:s|es)?(?:\\s|$)`).test(haystack);
    }
    return haystack.includes(variant);
  });
}

export function matchListingToCatalogProduct(
  listing: ListingMatchInput,
  catalog: CatalogProductMatchInput[],
  brandName?: string
): CatalogProductMatchInput | null {
  const haystack = normalize(
    [listing.title, listing.description, listing.product].filter(Boolean).join(' ')
  );
  if (!haystack || !catalog.length) return null;
  const brand = normalize(brandName || '');
  const brandPattern = brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
  const hayNoBrand = brandPattern ? haystack.replace(new RegExp(brandPattern, 'g'), ' ') : haystack;

  let best: { product: CatalogProductMatchInput; score: number } | null = null;
  for (const product of catalog) {
    const name = normalize(product.name);
    if (!name) continue;
    const generic = GENERIC_PRODUCT_TERMS.has(name);
    let score = 0;

    const identifier = normalize(product.identifier || '');
    if (identifier && haystack.includes(identifier)) {
      score += 100;
    }
    if (containsTerm(hayNoBrand, name) || containsTerm(haystack, name)) {
      score += generic ? 12 : 50 + Math.min(name.length, 24);
    }
    for (const keyword of product.keywords || []) {
      const kw = normalize(keyword);
      if (!kw || GENERIC_PRODUCT_TERMS.has(kw) || (brand && kw === brand)) continue;
      if (containsTerm(hayNoBrand, kw) || containsTerm(haystack, kw)) {
        score += generic ? 8 : 20;
      }
    }
    if (score > 0 && (!best || score > best.score)) {
      best = { product, score };
    }
  }
  if (!best || best.score < 12) return null;
  return best.product;
}
